import {
  Component,
  ViewEncapsulation,
  ViewChild,
  ChangeDetectorRef,
  EventEmitter,
  Output,
  Input,
  Renderer2,
  AfterViewInit,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { StorageService, ApiService } from '@dotgov/z_core';
import { FormBuilderHelper } from './../../../shared/helper';
import { BuilderTask, BuilderModel } from '../../models/builder.model';
import { BuilderService } from '../../../dnd/services/builder.service';

@Component({
  selector: 'fb-renderer',
  templateUrl: './renderer.component.html',
  styleUrls: ['./renderer.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FBRendererComponent extends StorageService implements AfterViewInit {
  loading: boolean;
  selectedTab: any;
  selectedTabIndex: number;
  loadingTemplates = true;
  activeSidebarTab = 'basic';

  private _formBuilderHelper = new FormBuilderHelper();
  private _activeTask: BuilderTask;
  private _controlDown: boolean;
  private _rendererSubscriptions = [];
  private _backupModels;

  @Output() reset = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<{ task; models }>();
  @ViewChild('formRenderer', { static: false }) formRenderer: NgForm;

  @Output() preview = new EventEmitter<{ task; models }>();

  @Input()
  set task(task: BuilderTask) {
    this._activeTask = task;
  }
  get task(): BuilderTask {
    return this._activeTask;
  }

  @Input() templateList: any;
  @Input() set activeTable(table: string) {
    if (table && table !== this.builderService.table) {
      this.loadTemplates(table);
    }
    this.builderService.table = table;
  }

  private _availableModels: BuilderModel;
  @Input()
  set availableModels(model: BuilderModel) {
    this._backupModels = JSON.stringify(model);
    this._availableModels = JSON.parse(this._backupModels);
  }
  get availableModels() {
    return this._availableModels;
  }

  get invalidTask() {
    return !this.task || !this.task.tabs || !this.task.tabs.length;
  }

  get newRow() {
    return [];
  }

  get activeDragging() {
    return this.builderService.anyActiveDragging;
  }

  get draggedModel() {
    return this.builderService.draggedModel;
  }

  get isTabActive() {
    return (this.draggedModel && this.draggedModel.sections) || (this.draggedModel.template && this.draggedModel.template.sections);
  }

  constructor(
    private ref: ChangeDetectorRef,
    private renderer: Renderer2,
    private builderService: BuilderService,
    private api: ApiService,
  ) {
    super();
    this._rendererSubscriptions.push(this.renderer.listen('document', 'keypress', this.documentKeydown.bind(this)));
    this._rendererSubscriptions.push(this.renderer.listen('document', 'keyup.control', this.documentKeyup.bind(this)));
  }

  ngAfterViewInit() {
    this.builderService.availableModels = this._availableModels;
    this.updateTabNamingHTML();
    this.loadingTemplates = false;
    this.ref.detectChanges();
  }

  onDrop(event) {
    if (!this.task || !this.task.tabs) {
      return;
    }
    const isTabDropped = event && event.value && event.value.template && event.value.template.sections;
    if (isTabDropped) {
      return this.onTabDropped(event.value.template);
    }
  }

  onTabDropped(tab) {
    this.task.tabs.push(tab);
    this.selectedTabIndex = this.task.tabs.length - 1;
    this.updateTabNamingHTML();
  }

  formNameChanged(newName) {
    this.task.header.title = newName;
  }

  tabNameChanged(tab, tabIndex: number, value: string) {
    if (!this.task || !this.task.tabs || !this.task.tabs.length) {
      return;
    }
    this.task.tabs[tabIndex].title = value;
    this.updateTabNamingHTML();
    this.ref.detectChanges();
  }

  addNewTab(container: any) {
    const newTabName = `New Tab ${container.tabs.length}`;
    container.tabs = [...(container.tabs || []), { controls: [], title: newTabName, name: newTabName }];
    this.selectedTabIndex = container.tabs.length - 1;
    this.updateTabNamingHTML();
  }

  isActive(tab: any, tabIndex?: number) {
    if (!this.selectedTabIndex) {
      return tabIndex === 0;
    }
    return tabIndex === this.selectedTabIndex;
  }

  removeTabHandler(tab: any, container: any, index: number) {
    if (window.confirm('Are you sure?')) {
      container.tabs.splice(index, 1);
      this.selectedTabIndex = container.tabs.length - 1;
      this.updateTabNamingHTML();
    }
    this.ref.detectChanges();
  }

  move(container, tabIndex, direction) {
    // Cannot move first to the left, last to right
    const lastLeft = tabIndex === 0;
    const lastRight = tabIndex === container.tabs.length - 1;
    if ((direction === 'left' && lastLeft) || (direction === 'right' && lastRight)) {
      return;
    }

    container.tabs = this._formBuilderHelper.move(container.tabs, tabIndex, direction === 'left' ? tabIndex - 1 : tabIndex + 1);
    this.updateTabNamingHTML();
    this.ref.detectChanges();
  }

  addTemplate(template, type?: string) {
    const table = this.builderService.table;
    if (!template || !table) {
      return;
    }
    if (!type) {
      type =
        (template.hasOwnProperty('cells') && 'row') ||
        (template.hasOwnProperty('sections') && 'tab') ||
        (template.hasOwnProperty('rows') && 'section');
    }
    if (!type) {
      return;
    }
    const name = window.prompt('Name your template');
    if (!name) {
      return;
    }
    // Allos only letter/numbers/_-
    const patt = /^[\w-]*[^\W_-][\w-]*$/gi;
    if (!patt.test(name)) {
      alert(`Template name can contain only letters, numbers and symbols minus/underscore.`);
      return;
    }
    if (this.templateList[type].indexOf(name) !== -1) {
      alert('Name already exist, choose some other name');
      return;
    }
    this.api.post('Designer', ['templates', table, type, name], {}, template).then(({ data, error }) => {
      if (error) {
        console.log(error);
        return;
      }
      this.loadTemplates(this.builderService.table);
    });
  }

  templateDelete({ template, type }) {
    console.log(type, template);
    if (!type || !template || !template.name) {
      return;
    }
    const table = this.builderService.table;
    this.api.delete('Designer', ['templates', table, type, template.name]).then(({ data, error }) => {
      if (error) {
        console.log(error);
        return;
      }
      this.loadTemplates(this.builderService.table);
    });
  }

  resetAll() {
    this.reset.emit(true);
  }

  saveAll(preview?: boolean) {
    if (this.invalidTask) {
      return;
    }
    const result: any = { models: this.availableModels, task: this.task };
    const { newTask, newModels } = this._formBuilderHelper.sanitizeTask(this.task);
    let models = [];
    if (newModels && newModels.length) {
      models = models.map(model => {
        const fieldIndex = this.availableModels.fieldCollection.findIndex(field => field.name === model.name);
        if (fieldIndex !== -1) {
          this.availableModels.fieldCollection[fieldIndex] = model;
          return null;
        }
        return model;
      });
      this.availableModels.fieldCollection.push(...models.filter(model => !!model));
    }
    if (JSON.stringify(newTask) !== JSON.stringify(this.task)) {
      result.task = newTask;
    }
    if (preview) {
      this.preview.emit(result);
    } else {
      this.save.emit(result);
    }
  }

  exportToJson() {
    const toExport = JSON.stringify(this.task);
    this.download(`${this.task.header.title || this.task.description.title}.json`, toExport);
  }

  importFromJson() {
    const element = document.createElement('input');
    element.setAttribute('type', 'file');

    element.style.display = 'none';
    document.body.appendChild(element);
    element.onchange = (event: any) => {
      event.stopPropagation();
      event.preventDefault();
      const file = event.target.files[0];
      if (!file) {
        return;
      }
      const reader = new FileReader();
      event.target.value = null;
      reader.readAsText(file);
      reader.onload = res => {
        this._activeTask = JSON.parse((res.target as any).result);
      };
    };

    element.click();

    document.body.removeChild(element);
  }

  uploadAsTemplate(tab) {
    this.addTemplate(tab, 'tab');
  }

  private download(filename, text) {
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
    element.setAttribute('download', filename);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  }

  private documentKeydown(event: KeyboardEvent) {
    if (event.keyCode === 17) {
      this._controlDown = true;
    }
    const numberedKey = Number(event.key);
    if (!Number.isNaN(numberedKey) && this._controlDown) {
      if (!this.task || !this.task.tabs || !this.task.tabs.length) {
        return;
      }
      event.preventDefault();
      const tab = this.task.tabs[numberedKey - 1];
      if (!tab) {
        return;
      }
      this.selectedTabIndex = numberedKey - 1;
    }
  }

  private documentKeyup() {
    this._controlDown = false;
  }

  private updateTabNamingHTML() {
    setTimeout(() => {
      const links = document.querySelectorAll('#formRenderer a.nav-link');
      links['forEach'](link => {
        link.removeAttribute('href');
      });
    }, 10);
  }

  private async loadTemplates(table) {
    this.loadingTemplates = true;
    this.ref.detectChanges();
    const templates = await this.api.get('Designer', ['templates', table], {}, { skipCache: true });
    this.templateList = templates.data;
    this.loadingTemplates = false;
    this.ref.detectChanges();
  }
}
